import React, { useState, useEffect, useRef } from 'react'

// For the search only version
import algoliasearch from 'algoliasearch/lite'
import playerjs from 'player.js'
import ScrollableAnchor, { configureAnchors, goToTop, goToAnchor } from 'react-scrollable-anchor'

import { useDebounce } from '../hooks'
import { db } from '../firebase'
import { BackArrowIcon, PlayIcon } from './Icon'
import './App.scss'

const client = algoliasearch(process.env.REACT_APP_ALGOLIA_APP_ID, process.env.REACT_APP_ALGOLIA_KEY)
const index = client.initIndex(process.env.REACT_APP_ALGOLIA_INDEX)

configureAnchors({offset: -70, scrollDuration: 200})

function PlayPodcast({ iframeSrc, time }) {
	const frameRef = useRef()

	useEffect(() => {
		if (frameRef.current) {
			const player = new playerjs.Player(frameRef.current)
			player.on('play', () => {
				if (time) {
					const timeSplit = time.split(':')
					const seconds = parseInt(timeSplit[0]) * 60 * 60 + parseInt(timeSplit[1]) * 60 + parseInt(timeSplit[2]) - 1
					player.setCurrentTime(seconds)	
				}
			})
		}

	}, [frameRef.current])


	return (
		<div className="audio">
			<iframe ref={frameRef} src={iframeSrc} style={{ width: '100%', height: '200px', border: '0 none' }} />
		</div>
	)
}
function MatchCard({ match, onMatchClick, loading }) {
	const [play, setPlay] = useState(false)
	const { id, date, title, text, iframeSrc, imgSrc } = match
	const content = match._highlightResult && match._highlightResult.text.value
	const time = text && text.slice(0, 7)

	return (
		<div className="matchCard" onClick={() => onMatchClick(id, time)}>
			{content && <div className="content" dangerouslySetInnerHTML={{ __html: content }}/>}
			<div className="info">
				<img src={imgSrc} />
				{loading && <div className="loadingCard"><div /><div /><div /><div /></div>}
				{!loading && <h3>{title}</h3>}
				{iframeSrc && 
				<div onClick={(e) => {
					e.stopPropagation()
					setPlay(!play)
				}}><PlayIcon play={play} /></div>}
			</div>
			{play && <PlayPodcast iframeSrc={iframeSrc} time={time} />}
		</div>
	)
}

function ActiveDocument({ document, time }) {
	const { content, iframeSrc } = document
	const [play, setPlay] = useState(false)
	useEffect(() => {
		if (document) {
			if (time) {
				goToAnchor(time)
			} else {
				goToTop()
			}
		}
	}, [document, time])

	const paragraphs = content.split('\n')
	const anchors = paragraphs.map((text, i) => (
		<ScrollableAnchor key={`${i}-${text.slice(0, 7)}`} id={text.slice(0, 7)}>
			<div>{text}</div>
		</ScrollableAnchor>
	)).slice(2, -1)

	return (
		<>
		<div className="activePodcast">
			{anchors}
		</div>
		<div className="activePlay" onClick={(e) => setPlay(!play)}><PlayIcon play={play} white={true} /></div>
		{play && <section className="activePlayer">
			<PlayPodcast iframeSrc={iframeSrc} />
		</section>}
		</>	
	)
}

export default function App() {
	const [query, setQuery] = useState('')
	const [podcasts, setPodcasts] = useState([])
	const [active, setActive] = useState({ id: null, document: null, time: null })
	const [matches, setMatches] = useState()
	const debouncedQuery = useDebounce(query, 500)

	useEffect(() => {
		async function fetchAllPodcasts() {
			const query = await db.collection("podcasts").orderBy("date", "desc").get()
			const podcasts = query.docs.map(doc => {
				const { title, imgSrc, iframeSrc, date } = doc.data()
				return { id: doc.id, title, date, imgSrc, iframeSrc }
			})
			setPodcasts(podcasts)
		}
	
		fetchAllPodcasts()
	}, [])

	useEffect(() => {
		async function fetchPodcasts() {
			const responses = await index.search(debouncedQuery)

			if (responses.hits.length) {
				setMatches(responses.hits)	
			} else {
				setMatches([])
			}
			goToTop()
		}

		if (debouncedQuery) {
			fetchPodcasts()
		} else {
			setMatches()
		}
	}, [debouncedQuery])

	useEffect(() => {
		async function fetchPodcast() {
			const document = await db.collection("podcasts").doc(active.id).get()
			setActive({ ...active, document: document.data() })
		}
		if (active.id) {
			fetchPodcast()
		}
	}, [active.id])

	const handleSearch = e => {
		setQuery(e.target.value)
	}

	const handleMatchClick = (id, time) => {
		setActive({ ...active, id, time })
	}

	const handleBack = () => {
		setActive({ id: null, document: null, time: null })
		window.location.hash = ''
		goToTop()
	}

  return (
    <div className="AppContainer">
      <div className="App">
				{!active.document && <div className="searchBar" >
					<input type="text" value={query} onChange={handleSearch} placeholder="Type to search" />
					{matches && matches.length > 0 && <span className="result-number">{matches.length}</span>}
					<img src="https://www.preposterousuniverse.com/wp-content/uploads/SCM-square-medium.jpg" />
				</div>}
				{active.document && <div className="searchBar activeSearchBar">
					<div className="back" onClick={handleBack}><BackArrowIcon /></div>
					<div className="title">
						<time dateTime={active.document.date}>{active.document.date}</time>
						<h3>{active.document.title}</h3>
					</div>
					<img src={active.document.imgSrc} />
				</div>}
				<div className="matches">
					{!podcasts.length && <MatchCard loading match={{ title: 'Loading podcasts...', date: '', imgSrc: 'https://www.preposterousuniverse.com/wp-content/uploads/SCM-square-medium.jpg'}} onMatchClick={() => {}} />}
					{active.id && !active.document && <MatchCard loading match={{ title: 'Loading podcast...', date: '', imgSrc: 'https://www.preposterousuniverse.com/wp-content/uploads/SCM-square-medium.jpg'}} onMatchClick={() => {}} />}
					{podcasts.length > 0 && !matches && !active.id && !active.document && podcasts.map(podcast => <MatchCard key={podcast.id} match={podcast} onMatchClick={handleMatchClick} />)}
					{matches && !matches.length && debouncedQuery && <p>No results, try something else</p>}
					{!active.document && matches && !active.id && !active.document && matches.map(match => <MatchCard key={match.objectId} match={match} onMatchClick={handleMatchClick} />)}
					{active.document && <ActiveDocument document={active.document} time={active.time} />}
				</div>
      </div>
    </div>
  )
}